import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { GraduationCap, ShieldCheck, Award, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import VideoPlaceholder from "@/components/VideoPlaceholder";
import { HOME_PAGE } from "@/constants/testIds";
import { useParticipant } from "@/context/ParticipantContext";

const DENSO_INTRO_VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_denso-cert-academy/artifacts/14c6s8ew_Denso%20Intro%20video.mp4";

const HIGHLIGHTS = [
  {
    icon: GraduationCap,
    title: "Learn the DENSO Range",
    text: "Master spark plugs, filters, wiper blades, service kits, horns and rotating electrics.",
  },
  {
    icon: ShieldCheck,
    title: "Sell With Confidence",
    text: "Learn how to handle objections and explain OE-quality value to every customer.",
  },
  {
    icon: Award,
    title: "Get Certified",
    text: "Score 90% or higher on the Q&A assessment to earn your Digital DENSO Certificate.",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { participant } = useParticipant();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-12">
      <motion.section
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center space-y-4"
      >
        <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-denso-red bg-red-50 px-3 py-1 rounded-full">
          Welcome{participant ? `, ${participant.name}` : ""}
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
          DENSO Product Training
        </h1>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          A truly interactive way to master DENSO products and services. Complete the training
          modules, explore the interactive product guide, and pass the Q&A assessment to earn your
          Digital Certificate of Completion.
        </p>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="grid sm:grid-cols-3 gap-4"
      >
        {HIGHLIGHTS.map((h, i) => (
          <div
            key={h.title}
            className="bg-white rounded-md border border-slate-200 p-6 space-y-3 transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
          >
            <div className="h-10 w-10 rounded-md bg-red-50 flex items-center justify-center">
              <h.icon className="h-5 w-5 text-denso-red" />
            </div>
            <h3 className="font-semibold text-slate-800">{h.title}</h3>
            <p className="text-sm text-slate-500 leading-relaxed">{h.text}</p>
          </div>
        ))}
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="space-y-4"
      >
        <div>
          <h2 className="font-heading text-2xl font-semibold text-slate-900">Introduction to DENSO</h2>
          <p className="text-sm text-slate-500 mt-1">
            Watch this short introduction before starting your training modules.
          </p>
        </div>
        <div data-testid={HOME_PAGE.introVideoPlaceholder}>
          <VideoPlaceholder
            moduleId="intro"
            title="Introduction to DENSO"
            videoUrl={DENSO_INTRO_VIDEO_URL}
            testIdPrefix="home-intro"
          />
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-denso-red rounded-xl p-8 text-center space-y-4"
      >
        <h2 className="font-heading text-2xl font-bold text-white">Ready to begin?</h2>
        <p className="text-white/85 text-sm max-w-lg mx-auto">
          Head to the Training Manual to start watching your product modules. Each module unlocks
          the next once completed.
        </p>
        <Button
          data-testid={HOME_PAGE.startTrainingButton}
          onClick={() => navigate("/training-manual")}
          className="bg-white text-denso-red hover:bg-slate-100 font-semibold h-11 px-6 transition-all duration-200 hover:-translate-y-0.5"
        >
          Start Training Manual <ArrowRight className="h-4 w-4" />
        </Button>
      </motion.section>
    </div>
  );
}
